import React, { useCallback, useEffect, useState } from 'react';

import Button from '@components/button/Button';
import CalendarInput from '@components/calendar-input/CalendarInput';
import Input from '@components/input/Input';
import TextArea from '@components/text-area/TextArea';
import TimePicker from '@components/time-picker/TimePicker';
import { STORAGE_KEYS } from '@constants/storage';
import {
  INPUT_BASE_CLASSNAME,
  INPUT_FOCUS_CLASSNAME
} from '@features/lead-management/constants/common';
import useNotify from '@hooks/notify';
import { appendTimeToISODate, formatISODate } from '@utils/date';
import { getLocalStorageItem, setLocalStorageItem } from '@utils/storage';

import {
  useCreateLeadEventMutation,
  useGetGoogleAuthStatusQuery,
  useLazyGetGoogleAuthUrlQuery
} from '../../queries/LeadManagementQuery';
import { EventFormData, EventModalProps } from '../../types/leadActivity';

const AddEventModal: React.FC<EventModalProps> = ({
  onClose,
  onSubmit,
  leadId,
  initialData = {}
}) => {
  const organisationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);
  const isEditing = Boolean(initialData.id);

  const { createErrorAlert, createSuccessAlert } = useNotify();

  const [createLeadEvent, { isLoading }] = useCreateLeadEventMutation();
  const { data: googleAuthData, isLoading: isLoadingGoogleAuth } =
    useGetGoogleAuthStatusQuery(
      { organizationId: organisationId },
      { skip: !organisationId }
    );
  const [getGoogleAuthUrl, { isLoading: isLoadingAuthUrl }] =
    useLazyGetGoogleAuthUrlQuery();

  const isConnected = googleAuthData?.is_authenticated || false;
  const [formData, setFormData] = useState<EventFormData>({
    name: '',
    date: '',
    startTime: '',
    endTime: '',
    location: '',
    remarks: '',
    ...initialData
  });
  const [error, setError] = useState<{ name: string }>({ name: '' });

  const handleInputChange = useCallback(
    (field: keyof EventFormData, value: any) => {
      if (field === 'name') {
        setError({ ...error, name: '' });
      }
      setFormData((prev) => ({
        ...prev,
        [field]: value
      }));
    },
    [error]
  );

  const isFormValid = useCallback(
    () =>
      !!(
        formData.name &&
        formData.date &&
        formData.startTime &&
        formData.endTime &&
        !error.name
      ),
    [formData, error]
  );

  const handleSubmit = useCallback(async () => {
    try {
      const dateTimestamp = appendTimeToISODate(formData.date, '00:00 AM');
      const startTimestamp = appendTimeToISODate(
        formData.date,
        formData.startTime
      );
      const endTimestamp = appendTimeToISODate(formData.date, formData.endTime);
      const submitData = {
        id: formData.id,
        name: formData.name,
        date: dateTimestamp,
        start_time: startTimestamp,
        end_time: endTimestamp,
        location: formData.location ? formData.location : null,
        remarks: formData.remarks ? formData.remarks : null
      };

      const response = await createLeadEvent({
        leadId,
        organizationId: organisationId,
        event: submitData
      }).unwrap();

      if (!('errors' in response)) {
        createSuccessAlert(
          `Meeting ${isEditing ? 'edited' : 'created'} successfully`
        );
        onSubmit();
      } else {
        createErrorAlert(`Failed to ${isEditing ? 'update' : 'create'} event`);
        onClose();
      }
    } catch (submitError) {
      createErrorAlert(`Failed to ${isEditing ? 'update' : 'create'} event`);
      onClose();
    }
  }, [
    formData,
    createLeadEvent,
    onClose,
    leadId,
    createErrorAlert,
    createSuccessAlert,
    organisationId,
    onSubmit,
    isEditing
  ]);

  const handleSyncGoogleCalendar = useCallback(async () => {
    if (isConnected) {
      createSuccessAlert('Google Calendar is already connected!');
    } else {
      try {
        const activityDetails = {
          type: 'event',
          data: {
            id: formData.id,
            name: formData.name,
            date: formData.date,
            startTime: formData.startTime,
            endTime: formData.endTime,
            location: formData.location,
            remarks: formData.remarks,
            leadId,
            organizationId: organisationId
          }
        };

        setLocalStorageItem(STORAGE_KEYS.ACTIVITY_DETAILS, activityDetails);

        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('success', 'true');
        const redirectUrl = encodeURIComponent(currentUrl.toString());
        const result = await getGoogleAuthUrl({
          organizationId: organisationId,
          redirectUrl
        }).unwrap();

        const authUrl = result?.auth_url;

        if (authUrl) {
          window.location.href = authUrl;
        } else {
          createErrorAlert('No auth URL received from server');
        }
      } catch (e) {
        createErrorAlert(
          'Failed to connect to Google Calendar. Please try again.'
        );
      }
    }
  }, [
    isConnected,
    getGoogleAuthUrl,
    organisationId,
    createSuccessAlert,
    createErrorAlert,
    formData,
    leadId
  ]);

  const handleClose = () => {
    onClose();
  };

  const handleInputBlur = (value: string) => {
    if (value.length < 3 || value.length > 30) {
      setError({
        ...error,
        name: 'Meeting name must be between 3 to 30 characters'
      });
    } else {
      setError({ ...error, name: '' });
    }
  };

  useEffect(() => {
    const activityDetails = getLocalStorageItem(STORAGE_KEYS.ACTIVITY_DETAILS);
    if (activityDetails?.type === 'event') {
      setFormData(activityDetails.data);
    }
  }, []);

  return (
    <>
      <div className="px-6 pb-1">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-gray-700">
            Meeting Name <span className="text-red-500">*</span>
          </label>
          <Input
            value={formData.name}
            handleInputChange={(value: string) =>
              handleInputChange('name', value)
            }
            placeholder="Name of the meeting"
            className="w-full"
            inputStyle={INPUT_BASE_CLASSNAME}
            handleInputBlur={handleInputBlur}
          />
          {error.name && (
            <p className="mt-1 text-sm text-red-500">{error.name}</p>
          )}
        </div>

        <div className="mt-2 space-y-1.5">
          <label className="text-sm font-medium text-gray-700">
            Date <span className="text-red-500">*</span>
          </label>
          <div className="flex h-12.5 items-center">
            <CalendarInput
              selectedDate={formData.date ? new Date(formData.date) : undefined}
              handleDateChange={(date?: Date) =>
                handleInputChange(
                  'date',
                  date
                    ? formatISODate({
                        date,
                        separator: '-',
                        format: 'yyyy-mm-dd'
                      })
                    : ''
                )
              }
              placeholder="Select Date"
              className="w-full [&_input]:!flex [&_input]:!h-12.5 [&_input]:!items-center"
              isMaxDateRequired={false}
              inputStyle={INPUT_BASE_CLASSNAME}
            />
          </div>
        </div>

        <div className="mt-2 grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              Start Time <span className="text-red-500">*</span>
            </label>
            <TimePicker
              label=""
              time={formData.startTime}
              handleChange={(value: string) =>
                handleInputChange('startTime', value)
              }
              labelStyle="hidden"
              className="w-full"
              popupClassName="!fixed !top-1/2 !left-1/2 !transform !-translate-x-1/2 !-translate-y-1/2 !z-[9999]"
              pickerInputStyles={`${INPUT_BASE_CLASSNAME} ${INPUT_FOCUS_CLASSNAME}`}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              End Time <span className="text-red-500">*</span>
            </label>
            <TimePicker
              label=""
              time={formData.endTime}
              handleChange={(value: string) =>
                handleInputChange('endTime', value)
              }
              labelStyle="hidden"
              className="w-full"
              popupClassName="!fixed !top-1/2 !left-1/2 !transform !-translate-x-1/2 !-translate-y-1/2 !z-[9999]"
              pickerInputStyles={`${INPUT_BASE_CLASSNAME} ${INPUT_FOCUS_CLASSNAME}`}
            />
          </div>
        </div>

        <div className="mt-2 space-y-1.5">
          <label className="text-sm font-medium text-gray-700">Location</label>
          <Input
            value={formData.location}
            handleInputChange={(value: string) =>
              handleInputChange('location', value)
            }
            placeholder="Location of the meeting"
            className="w-full"
            inputStyle={INPUT_BASE_CLASSNAME}
          />
        </div>

        <div className="mt-2 space-y-1.5">
          <label className="text-sm font-medium text-gray-700">Remarks</label>
          <TextArea
            value={formData.remarks}
            onChange={(value: string) => handleInputChange('remarks', value)}
            placeholder="Add a note"
            rows={3}
            className="w-full"
            inputStyle={INPUT_BASE_CLASSNAME}
          />
        </div>
        {!isConnected && !isLoadingGoogleAuth && (
          <Button
            className="cursor-pointer !p-0 text-base font-medium text-[#1C80D5]"
            handleClick={handleSyncGoogleCalendar}
            variant="transparent"
          >
            Sync Google Calendar
          </Button>
        )}
      </div>
      <div className="flex gap-4 rounded-b-xl border-t border-gray-200 p-6">
        <Button
          variant="secondary"
          handleClick={handleClose}
          label="Cancel"
          className="flex-1"
        />
        <Button
          handleClick={handleSubmit}
          label="Done"
          className="flex-1"
          loading={isLoading || isLoadingAuthUrl}
          disabled={!isFormValid()}
        />
      </div>
    </>
  );
};

export default AddEventModal;
